import useSWR from 'swr';

import { fetchMediaAccess, type MediaAccessResponse } from '../services/mediaAccess';

/** Refetch this long before `expiresAt` so the signed query string never goes stale mid-use. */
const REFRESH_BUFFER_MS = 60_000;

type MediaAccessKey = readonly ['media-access', string, string, number];

function mediaAccessKey(
  submissionId?: string,
  version?: string,
  revision?: number
): MediaAccessKey | null {
  return submissionId && version && revision !== undefined
    ? (['media-access', submissionId, version, revision] as const)
    : null;
}

/**
 * Fetches the signed media-access grant for one submission/version/revision and keeps
 * it fresh across its ~1-hour expiry. Every figure sharing the same
 * `submissionId`/`version`/`revision` shares one request and one cached grant via SWR's
 * key-based cache — the query string is fetched once per document, not once
 * per image.
 */
export function useMediaAccess(
  submissionId?: string,
  version?: string,
  revision?: number
) {
  const { data, error, isLoading } = useSWR<MediaAccessResponse>(
    mediaAccessKey(submissionId, version, revision),
    ([, sid, ver, rev]: MediaAccessKey) =>
      fetchMediaAccess({ submissionId: sid, version: ver, revision: rev }),
    {
      revalidateOnFocus: false,
      refreshInterval: (latest) =>
        latest
          ? Math.max(0, latest.expiresAt * 1000 - Date.now() - REFRESH_BUFFER_MS)
          : 0,
    }
  );

  return { mediaAccess: data, error, isLoading };
}
